/**
 * Agent history & reset — every change to the agent's brain, with real rollback, and a protected
 * "reset to the starter template" with undo.
 *
 * History: the server records a version of every brain item (opportunity maps, knowledge categories,
 * strategies, rule sets, scenario flows, teachings, brain contexts, live-check sources) whenever it
 * changes — from the dashboard, Synthora, self-learning or iMaven's CRM. Usage counters are not changes.
 * Clicking a change opens the item's versions with what changed in each; "Restore this version" writes
 * it back (a restore is itself a new version, so it can be undone the same way).
 *
 * Reset: shows what will be lost first (items not from the template, template items you edited, your
 * instructions), asks twice (the second time the agent's name must be typed), and keeps a full backup
 * for 30 days — "Undo reset" puts the brain back exactly as it was.
 *
 * API (Maven-platform-Server routes/brainHistoryRoutes.ts): GET /brain-history/recent ·
 *   GET /brain-history/items/:collection/:docId/versions · POST /brain-history/versions/:id/restore ·
 *   GET /brain-history/reset/preview · POST /brain-history/reset {confirmName} ·
 *   GET /brain-history/reset/backups · POST /brain-history/reset/backups/:id/undo
 */

import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import { useToast } from '../Context/ToastContext';

// ============================================
// STYLES (dashboard dark palette, as AgentPage)
// ============================================
const Page = styled.div` height:100%; overflow-y:auto; background:#0f1419; color:#e6edf3; padding:28px 32px; @media (max-width:700px){ padding:20px 16px; } `;
const Title = styled.h1` font-size:24px; margin:0; font-weight:600; `;
const Subtitle = styled.p` color:#8b98a5; margin:4px 0 24px; font-size:14px; max-width:820px; line-height:1.5; `;
const Grid = styled.div` display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1.2fr); gap:16px; @media (max-width:1100px){ grid-template-columns:1fr; } `;
const Card = styled.div` background:#161b22; border:1px solid rgba(255,255,255,0.08); border-radius:12px; padding:18px 20px; margin-bottom:16px; min-width:0; `;
const DangerCard = styled(Card)` border-color:rgba(248,81,73,0.35); `;
const CardTitle = styled.h3` margin:0 0 12px; font-size:15px; font-weight:600; color:#e6edf3; display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; `;
const Input = styled.input` width:100%; background:#0f1419; border:1px solid rgba(255,255,255,0.12); color:#e6edf3; border-radius:8px; padding:10px 12px; font-size:14px; outline:none; &:focus{border-color:#2f81f7;} `;
const GhostBtn = styled.button` background:transparent; color:#e6edf3; border:1px solid rgba(255,255,255,0.14); border-radius:8px; padding:9px 16px; font-size:13px; font-weight:600; cursor:pointer; &:hover{background:rgba(255,255,255,0.06);} &:disabled{opacity:.5;cursor:default;} `;
const DangerBtn = styled(GhostBtn)` color:#f85149; border-color:rgba(248,81,73,0.4); `;
const SolidDangerBtn = styled.button` background:#da3633; color:#fff; border:none; border-radius:8px; padding:9px 16px; font-size:13px; font-weight:600; cursor:pointer; &:hover{background:#f85149;} &:disabled{opacity:.45;cursor:default;} `;
const SmallBtn = styled(GhostBtn)` padding:5px 10px; font-size:12px; `;
const Row = styled.div` display:flex; gap:10px; flex-wrap:wrap; align-items:center; `;
const Help = styled.div` color:#8b98a5; font-size:12px; margin-top:6px; line-height:1.5; `;
const Pill = styled.span` display:inline-block; padding:2px 9px; border-radius:999px; font-size:11px; font-weight:600; white-space:nowrap; background:${p => p.$bg || 'rgba(255,255,255,0.08)'}; color:${p => p.$fg || '#e6edf3'}; `;
const Chip = styled.button` border:1px solid ${p => p.$on ? '#2f81f7' : 'rgba(255,255,255,0.14)'}; background:${p => p.$on ? 'rgba(47,129,247,0.18)' : 'transparent'}; color:#e6edf3; border-radius:999px; padding:4px 11px; font-size:12px; cursor:pointer; `;
const ChangeRow = styled.button` width:100%; text-align:left; display:grid; grid-template-columns:auto minmax(0,1fr) auto; gap:10px; align-items:center; padding:9px 8px; border:none; border-top:1px solid rgba(255,255,255,0.06); background:${p => p.$active ? 'rgba(47,129,247,0.12)' : 'transparent'}; color:#e6edf3; font-size:13px; cursor:pointer; &:hover{background:rgba(255,255,255,0.04);} `;
const Muted = styled.span` color:#8b98a5; font-size:12px; `;
const Ellipsis = styled.div` overflow:hidden; text-overflow:ellipsis; white-space:nowrap; `;
const VersionBox = styled.div` border:1px solid rgba(255,255,255,0.08); border-radius:10px; padding:12px 14px; margin-top:10px; background:#0f1419; `;
const DiffLine = styled.div` font-size:12px; line-height:1.5; padding:6px 0; border-top:1px dashed rgba(255,255,255,0.06); word-break:break-word; `;
const DiffPath = styled.div` color:#8b98a5; font-family:ui-monospace,Menlo,Consolas,monospace; font-size:11px; `;
const Before = styled.div` color:#ff7b72; white-space:pre-wrap; `;
const After = styled.div` color:#56d364; white-space:pre-wrap; `;
const Table = styled.table` width:100%; border-collapse:collapse; font-size:13px; th,td{ text-align:left; padding:6px 8px; border-top:1px solid rgba(255,255,255,0.06); } th{ color:#8b98a5; font-weight:600; font-size:12px; } td.n{ text-align:right; font-variant-numeric:tabular-nums; } `;
const Overlay = styled.div` position:fixed; inset:0; background:rgba(0,0,0,0.6); display:flex; align-items:center; justify-content:center; z-index:2000; padding:16px; `;
const Dialog = styled.div` background:#161b22; border:1px solid rgba(248,81,73,0.45); border-radius:12px; padding:22px 24px; width:100%; max-width:520px; color:#e6edf3; box-shadow:0 20px 60px rgba(0,0,0,0.5); h3{margin:0 0 10px; font-size:17px;} p{color:#c9d1d9; font-size:14px; line-height:1.55; margin:0 0 10px;} `;

const COLLECTIONS = [
  ['', 'All'],
  ['teachings', 'Teachings'],
  ['opportunitymaps', 'Opportunity maps'],
  ['expertstrategies', 'Strategies'],
  ['knowledgecategories', 'Knowledge'],
  ['rulesets', 'Rule sets'],
  ['scenarioflows', 'Scenario flows'],
  ['braincontexts', 'Brain contexts'],
  ['agentlivesources', 'Live-check sources'],
];
const OP_TEXT = {
  insert: ['Added', 'rgba(63,185,80,0.2)', '#56d364'],
  update: ['Edited', 'rgba(47,129,247,0.2)', '#79c0ff'],
  replace: ['Replaced', 'rgba(47,129,247,0.2)', '#79c0ff'],
  delete: ['Deleted', 'rgba(248,81,73,0.2)', '#ff7b72'],
  baseline: ['Recorded', 'rgba(139,152,165,0.2)', '#c9d1d9'],
};
const fmtWhen = (d) => (d ? new Date(d).toLocaleString() : '');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');
const errText = (e, fallback) => e?.response?.data?.error || fallback;
const sameName = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
const opPill = (v) => {
  const [t, bg, fg] = v.restoredFrom ? ['Restored', 'rgba(210,153,34,0.2)', '#e3b341'] : (OP_TEXT[v.op] || [v.op, null, null]);
  return <Pill $bg={bg} $fg={fg}>{t}</Pill>;
};
const actorText = (a) => {
  if (!a) return '';
  if (a === 'template') return 'from the starter template';
  if (a === 'learned') return 'self-learning';
  return a;
};

// ============================================
// Item versions (opened from a change)
// ============================================
function ItemVersions({ api, showToast, item, onRestored }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/brain-history/items/${item.collection}/${encodeURIComponent(item.docId)}/versions`);
      setData(d);
      if (d.versions?.[0]) setOpen({ [d.versions[0].id]: true });
    } catch (e) { showToast(errText(e, 'Could not load the versions'), 'error'); setData(null); }
    setLoading(false);
  }, [api, item, showToast]);
  useEffect(() => { load(); }, [load]);

  const restore = async (v) => {
    const what = data?.exists ? `replace the current "${data.itemName}" with version ${v.version} from ${fmtWhen(v.at)}` : `bring back "${data?.itemName}" as it was in version ${v.version}`;
    if (!window.confirm(`Restore this version?\n\nThis will ${what}. The current content is kept in the history, so you can go back to it.`)) return;
    setBusy(v.id);
    try {
      const { data: r } = await api.post(`/brain-history/versions/${v.id}/restore`);
      showToast(r.recreated ? `"${r.itemName}" is back (version ${r.version}).` : `"${r.itemName}" restored to version ${r.version}.`, 'success');
      // The new version is recorded by the server's watcher a moment later.
      setTimeout(() => { load(); onRestored && onRestored(); }, 1500);
    } catch (e) { showToast(errText(e, 'Could not restore this version'), 'error'); }
    setBusy('');
  };

  if (loading) return <Help>Loading versions…</Help>;
  if (!data) return null;
  return (
    <div>
      <Row style={{ justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{data.itemName}</div>
          <Muted>{item.collectionLabel} · {data.versions.length} version{data.versions.length === 1 ? '' : 's'}{data.exists ? '' : ' · currently deleted'}</Muted>
        </div>
        <SmallBtn onClick={load}>Refresh</SmallBtn>
      </Row>
      {data.versions.map((v) => (
        <VersionBox key={v.id}>
          <Row style={{ justifyContent: 'space-between' }}>
            <Row>
              <b>v{v.version}</b> {opPill(v)}
              {v.isCurrent && <Pill $bg="rgba(63,185,80,0.2)" $fg="#56d364">Current</Pill>}
              <Muted>{fmtWhen(v.at)}{v.actor ? ` · ${actorText(v.actor)}` : ''}{v.restoredFrom ? ` · from v${v.restoredFrom.version}` : ''}</Muted>
            </Row>
            <Row>
              {v.op !== 'delete' && v.changeCount > 0 && (
                <SmallBtn onClick={() => setOpen((o) => ({ ...o, [v.id]: !o[v.id] }))}>{open[v.id] ? 'Hide changes' : `Show changes (${v.changeCount})`}</SmallBtn>
              )}
              {v.op !== 'delete' && !v.isCurrent && !v.truncated && (
                <SmallBtn disabled={!!busy} onClick={() => restore(v)}>{busy === v.id ? 'Restoring…' : 'Restore this version'}</SmallBtn>
              )}
            </Row>
          </Row>
          {v.op === 'delete' && <Help>The item was deleted here. Restore the version before this one to bring it back.</Help>}
          {open[v.id] && v.op !== 'delete' && (
            <div style={{ marginTop: 8 }}>
              {v.changes.map((c) => (
                <DiffLine key={c.path}>
                  <DiffPath>{c.path}</DiffPath>
                  {c.before !== undefined && c.before !== null && <Before>− {c.before}</Before>}
                  {c.after !== undefined && c.after !== null && <After>+ {c.after}</After>}
                </DiffLine>
              ))}
              {v.changeCount > v.changes.length && <Help>…and {v.changeCount - v.changes.length} more changed fields.</Help>}
            </div>
          )}
        </VersionBox>
      ))}
    </div>
  );
}

// ============================================
// Reset danger zone
// ============================================
function ResetZone({ api, showToast, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0); // 0 none · 1 first confirm · 2 type the name
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, b] = await Promise.all([api.get('/brain-history/reset/preview'), api.get('/brain-history/reset/backups')]);
      setPreview(p.data);
      setBackups(b.data.backups || []);
    } catch (e) { showToast(errText(e, 'Could not load the reset preview'), 'error'); }
    setLoading(false);
  }, [api, showToast]);
  useEffect(() => { load(); }, [load]);

  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/brain-history/reset', { confirmName: typed });
      setResult(data);
      setStep(0); setTyped('');
      showToast(`The agent was reset to "${data.template?.name}". A backup is kept until ${fmtDate(data.expiresAt)}.`, 'success');
      await load(); onChanged && onChanged();
    } catch (e) { showToast(errText(e, 'The reset failed'), 'error'); }
    setBusy(false);
  };

  const undo = async (b) => {
    const msg = b.reason === 'undo'
      ? `Go back to the brain as it was before you undid a reset on ${fmtWhen(b.createdAt)}?`
      : `Undo the reset of ${fmtWhen(b.createdAt)}?\n\nThe agent's brain goes back exactly as it was before that reset (${b.items} items). What the agent has now is backed up first, so this can be undone too.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.post(`/brain-history/reset/backups/${b.id}/undo`);
      setResult(null);
      showToast('Done — the agent\'s brain is back as it was.', 'success');
      await load(); onChanged && onChanged();
    } catch (e) { showToast(errText(e, 'Could not undo the reset'), 'error'); }
    setBusy(false);
  };

  const t = preview?.totals || {};
  const lostCount = (t.notFromTemplate || 0) + (t.editedTemplateItems || 0);
  return (
    <DangerCard>
      <CardTitle><span style={{ color: '#ff7b72' }}>Reset agent to starter template</span><SmallBtn onClick={load} disabled={loading}>Refresh</SmallBtn></CardTitle>
      {loading && !preview ? <Help>Loading…</Help> : preview && (
        <>
          <Help style={{ marginTop: 0, fontSize: 13 }}>
            Replaces your agent's whole brain with a fresh copy of the starter template
            {preview.template ? <> <b style={{ color: '#e6edf3' }}>{preview.template.name}</b> (version {preview.template.version})</> : ''}.
            Everything you taught it is removed. A full backup is kept for {preview.backupDays} days so you can undo.
          </Help>
          {!preview.template && <Help style={{ color: '#ff7b72' }}>No starter template applies to this agent, so it cannot be reset.</Help>}
          <Table style={{ marginTop: 12 }}>
            <thead><tr><th>Brain part</th><th className="n">Items now</th><th className="n">Not from the template</th><th className="n">Template items you edited</th></tr></thead>
            <tbody>
              {preview.collections.filter((c) => c.total > 0).map((c) => (
                <tr key={c.collection}><td>{c.label}</td><td className="n">{c.total}</td><td className="n">{c.notFromTemplate || '–'}</td><td className="n">{c.editedTemplateItems || '–'}</td></tr>
              ))}
              <tr><td><b>Total</b></td><td className="n"><b>{t.items}</b></td><td className="n"><b>{t.notFromTemplate}</b></td><td className="n"><b>{t.editedTemplateItems}</b></td></tr>
            </tbody>
          </Table>
          {preview.instructionsChanged && <Help>Your agent's instructions differ from the template's and will be replaced too.</Help>}
          <Row style={{ marginTop: 14 }}>
            <DangerBtn disabled={!preview.canReset || busy} onClick={() => { setStep(1); setTyped(''); }}>Reset agent to starter template…</DangerBtn>
          </Row>
        </>
      )}

      {result && (
        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 10, background: 'rgba(63,185,80,0.08)', border: '1px solid rgba(63,185,80,0.3)' }}>
          <div style={{ fontWeight: 600 }}>Reset done</div>
          <Help>The brain is now "{result.template?.name}" v{result.template?.version}. Backup kept until {fmtDate(result.expiresAt)}.</Help>
          <Row style={{ marginTop: 8 }}>
            <GhostBtn disabled={busy} onClick={() => { const b = backups.find((x) => x.id === result.backupId); if (b) undo(b); }}>Undo reset</GhostBtn>
          </Row>
        </div>
      )}

      {backups.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Backups</div>
          <Table>
            <thead><tr><th>When</th><th>What</th><th className="n">Items</th><th>Kept until</th><th /></tr></thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.id}>
                  <td>{fmtWhen(b.createdAt)}</td>
                  <td>
                    {b.reason === 'reset' ? 'Before reset' : 'Before undo'}{' '}
                    {b.status === 'undone' && <Pill>Undone</Pill>}
                    {b.status === 'rolled-back' && <Pill $bg="rgba(248,81,73,0.2)" $fg="#ff7b72">Reset failed — restored</Pill>}
                  </td>
                  <td className="n">{b.items}</td>
                  <td>{fmtDate(b.expiresAt)}</td>
                  <td>{b.canUndo && <SmallBtn disabled={busy} onClick={() => undo(b)}>{b.reason === 'reset' ? 'Undo reset' : 'Restore'}</SmallBtn>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {step === 1 && preview && (
        <Overlay onClick={() => setStep(0)}>
          <Dialog onClick={(e) => e.stopPropagation()}>
            <h3>Reset "{preview.agentName}"?</h3>
            <p>The agent's brain ({t.items} items) will be deleted and replaced by the starter template <b>{preview.template?.name}</b> v{preview.template?.version}.</p>
            <p>{lostCount > 0
              ? <>You will lose <b>{t.notFromTemplate}</b> item{t.notFromTemplate === 1 ? '' : 's'} that did not come from the template and your edits to <b>{t.editedTemplateItems}</b> template item{t.editedTemplateItems === 1 ? '' : 's'}.</>
              : <>The agent has no items of its own and no edited template items.</>}
            {preview.instructionsChanged ? ' Your instructions will be replaced too.' : ''}</p>
            <p>A full backup is kept for {preview.backupDays} days; you can undo the reset during that time.</p>
            <Row style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <GhostBtn onClick={() => setStep(0)}>Cancel</GhostBtn>
              <SolidDangerBtn onClick={() => setStep(2)}>Continue</SolidDangerBtn>
            </Row>
          </Dialog>
        </Overlay>
      )}
      {step === 2 && preview && (
        <Overlay onClick={() => !busy && setStep(0)}>
          <Dialog onClick={(e) => e.stopPropagation()}>
            <h3>Type the agent's name to confirm</h3>
            <p>To reset, type <b>{preview.agentName}</b> below.</p>
            <Input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={preview.agentName}
              onKeyDown={(e) => { if (e.key === 'Enter' && sameName(typed, preview.agentName) && !busy) doReset(); }} />
            <Row style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <GhostBtn disabled={busy} onClick={() => setStep(0)}>Cancel</GhostBtn>
              <SolidDangerBtn disabled={busy || !sameName(typed, preview.agentName)} onClick={doReset}>{busy ? 'Resetting…' : 'Reset the agent'}</SolidDangerBtn>
            </Row>
          </Dialog>
        </Overlay>
      )}
    </DangerCard>
  );
}

// ============================================
// Page
// ============================================
export default function AgentHistoryPage() {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const loadRecent = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/brain-history/recent', { params: { limit: 150, ...(filter ? { collection: filter } : {}) } });
      setChanges(data.changes || []);
    } catch (e) { showToast(errText(e, 'Could not load the history'), 'error'); }
    setLoading(false);
  }, [api, filter, showToast]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  return (
    <Page>
      <Row style={{ justifyContent: 'space-between' }}>
        <Title>Agent history &amp; reset</Title>
        <GhostBtn onClick={() => navigate('/agent')}>← My AI Agents</GhostBtn>
      </Row>
      <Subtitle>
        Every change to your agent's brain is kept — by you, by self-learning or by iMaven — so any item can be
        rolled back to an earlier version. Changes are kept for 90 days, and always the last 20 per item.
      </Subtitle>
      <Grid>
        <Card>
          <CardTitle>Recent changes <SmallBtn onClick={loadRecent} disabled={loading}>Refresh</SmallBtn></CardTitle>
          <Row style={{ marginBottom: 10 }}>
            {COLLECTIONS.map(([k, label]) => <Chip key={k || 'all'} $on={filter === k} onClick={() => { setFilter(k); setSelected(null); }}>{label}</Chip>)}
          </Row>
          {loading && !changes.length ? <Help>Loading…</Help> : !changes.length ? (
            <Help>No changes recorded yet. Changes appear here as soon as the agent's brain is edited.</Help>
          ) : changes.map((c) => (
            <ChangeRow key={c.id} $active={selected?.docId === c.docId && selected?.collection === c.collection} onClick={() => setSelected(c)}>
              {opPill(c)}
              <div style={{ minWidth: 0 }}>
                <Ellipsis>{c.itemName}</Ellipsis>
                <Muted>{c.collectionLabel}{c.actor ? ` · ${actorText(c.actor)}` : ''}</Muted>
              </div>
              <Muted>{fmtWhen(c.at)}</Muted>
            </ChangeRow>
          ))}
        </Card>
        <div style={{ minWidth: 0 }}>
          <Card>
            <CardTitle>Versions</CardTitle>
            {selected
              ? <ItemVersions key={`${selected.collection}:${selected.docId}`} api={api} showToast={showToast} item={selected} onRestored={loadRecent} />
              : <Help style={{ marginTop: 0 }}>Click a change to see every version of that item, what changed, and restore an earlier one.</Help>}
          </Card>
          <ResetZone api={api} showToast={showToast} onChanged={() => { setSelected(null); setTimeout(loadRecent, 1500); }} />
        </div>
      </Grid>
    </Page>
  );
}
